"use client";

import "./partners.css";
import HomeStats from "../HomeStats";
import PartnerSlider from "./PartnerSlider";
import { useEffect, useRef } from "react";
import { animateBadge } from "src/utils/animation/animateBadge";
import { clients, partners } from "src/utils/mock/homePageMock";
import SectionHeading from "src/components/molecules/SectionHeading";

function PartnerCard({ image, name, delay }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    ref.current.style.transform = `translate(${x * 0.08}px, ${y * 0.08}px) translateY(-5px)`;
  };

  const reset = () => {
    ref.current.style.transform = "";
  };

  return (
    <div ref={ref} onMouseMove={handleMove} onMouseLeave={reset} className="sp-partner-card reveal" style={{ transitionDelay: `${delay}ms` }}>
      <span className="sp-card-top-line" />

      <div className="sp-logo-wrapper">
        <img src={image} alt={name} />
      </div>
    </div>
  );
}

function ClientCard({ image, name }) {
  const ref = useRef(null);

  const handleMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    ref.current.style.transform = `translate(${x * 0.08}px, ${y * 0.08}px) translateY(-5px)`;
  };

  const reset = () => {
    ref.current.style.transform = "";
  };

  return (
    <div ref={ref} onMouseMove={handleMove} onMouseLeave={reset} className="sp-partner-card">
      <span className="sp-card-top-line" />

      <div className="sp-logo-wrapper">
        <img src={image} alt={name} />
      </div>
    </div>
  );
}

function GroupLabel({ children }) {
  return (
    <div className="sp-group-label reveal">
      <span className="sp-line" />

      <h3 className="sp-group-title">{children}</h3>

      <span className="sp-line" />
    </div>
  );
}

export default function StrategicPartnersSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { gsap } = await import("gsap");
        const { ScrollTrigger } = await import("gsap/ScrollTrigger");
        gsap.registerPlugin(ScrollTrigger);

        let SplitText, chroma;
        try {
          ({ SplitText } = await import("gsap/SplitText"));
          gsap.registerPlugin(SplitText);
        } catch { }
        try {
          chroma = (await import("chroma-js")).default;
        } catch { }

        if (SplitText && chroma) {
          const badgeEl = sectionRef.current?.querySelector(".services-badge");
          if (badgeEl) animateBadge(badgeEl);
        }

        const orbs = sectionRef.current?.querySelectorAll(".bg-orb");
        if (orbs?.length) {
          orbs.forEach((orb, i) => {
            gsap.to(orb, {
              y: i % 2 === 0 ? -80 : 80,
              ease: "none",
              scrollTrigger: {
                trigger: sectionRef.current,
                start: "top bottom",
                end: "bottom top",
                scrub: 1.5,
              },
            });
          });
        }
      } catch (err) {
        console.warn("[StrategicPartnersSection] GSAP animation skipped:", err.message);
      }
    })();

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle("sp-revealed", entry.isIntersecting);
        });
      },
      { threshold: 0.1 },
    );

    sectionRef.current?.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const partnersLoop = [...partners, ...partners];
  const clientsLoop = [...clients, ...clients];

  return (
    <>
      <section ref={sectionRef} className="sp-section">
        <div
          className="bg-orb"
          style={{
            position: "absolute",
            top: "10%",
            left: "-8%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(59,91,219,0.07) 0%, transparent 70%)",
            pointerEvents: "none",
            filter: "blur(40px)",
          }}
        />
        <div
          className="bg-orb"
          style={{
            position: "absolute",
            bottom: "5%",
            right: "-5%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(0,180,216,0.06) 0%, transparent 70%)",
            pointerEvents: "none",
            filter: "blur(50px)",
          }}
        />
        <div
          className="bg-orb"
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            transform: "translate(-50%,-50%)",
            background: "radial-gradient(circle, rgba(59,91,219,0.05) 0%, transparent 70%)",
            pointerEvents: "none",
            filter: "blur(30px)",
          }}
        />

        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            backgroundImage: "radial-gradient(circle, rgba(0,0,0,0.06) 1px, transparent 1px)",
            backgroundSize: "28px 28px",
            maskImage: "radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)",
          }}
        />

        <div className="container !py-10 sm:!py-8  lg:!py-10 xl:!py-12" style={{ position: "relative", zIndex: 1 }}>
          <SectionHeading
            title="Delivering Innovative Solutions"
            subtitle="Since inception, we have been delivering innovative web and mobile solutions tailored to modern business needs worldwide. Our experienced team focuses on quality, performance, and customer satisfaction, helping businesses achieve digital growth through reliable, scalable, and user-focused technology solutions. Every successful project reflects our commitment to innovation, excellence, and long-term client partnerships."
            maxWidth={1200}
            miniTitle="Strategic"
            miniHighlight="Partners"
          />
          <div style={{ width: "100%", margin: "auto" }}>
            <GroupLabel>Happy Clients</GroupLabel>
            <HomeStats />
          </div>

          <div style={{ width: "100%", margin: "auto" }}>
            <GroupLabel>Partners</GroupLabel>
            <PartnerSlider items={partnersLoop} />
          </div>
        </div>
      </section>
    </>
  );
}
